import React, { useCallback, useEffect, useState } from "react";
import DOMPurify from "dompurify";
import AssessmentGuideline from "./guideline";
import RetryAssessment from "./retry";
import CallToAction from "../partials/cta";
import CircularProgress from "@mui/material/CircularProgress";
import NotificationMessage from "../../../components/notification";
import SectionTitle from "../../../components/sectionTitle";
import { Alert, Box, Typography } from "@mui/material";
import { useHistory } from "react-router";
import { postWebService } from "../../../helpers/server";
import { buildWrapperTokenHeader } from "../../../helpers/lead";
import {
  getRoute,
  getStorageItem,
  makeid,
  removeStorageItem,
  setStorageItem,
} from "../../../helpers/utils";
import {
  buildCheckSumPayload,
  buildOrderIdPayload,
} from "../../../helpers/bank-details";
import { useStyles } from "../indexFormStyles";
import { withStyles } from "@mui/styles";
import { Utils } from "../../../constants/utils";
import { SLUG_OPEN, SLUG_RETRY, SLUG_TERMINATE } from "../../../constants";

function BuyflowHealthAssessment({
  classes: { containerButton, main, loaderBox, formGroup },
}) {
  const current = new Date();
  const orderID = Number(getStorageItem("oid"));
  const scanID = current.getTime();
  const history = useHistory();
  const [fedohealthcheck, setFedohealthcheck] = useState(false);
  const [errors, setErrors] = useState([]);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [modal, setModal] = useState({
    [SLUG_OPEN]: false,
    [SLUG_RETRY]: false,
  });
  const [urlSafe, setUrlSafe] = useState(null);
  const [healthVitals, setHealthVitals] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [healthConfig, setHealthConfig] = useState(null);
  const [healthBlock, setHealthBlock] = useState({
    options: true,
    scan_now: false,
    scan_link: false,
  });
  const [isButtonClicked, setIsButtonClicked] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [form, setForm] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [resendLink, setResendLink] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    wrapperToken();
    removeStorageItem("healthVitals");
  }, []);

  useEffect(() => {}, [urlSafe]);

  const cbSuccessWrapperToken = ({ access_token }, fetchConfig) => {
    setStorageItem("access-token", access_token);
    setStorageItem("scanID", scanID);
    setUrlSafe(
      DOMPurify.sanitize(
        `${process.env.REACT_APP_API_EP}careplix.php?oid=${orderID}`
      )
    );
    if (fetchConfig) {
      fetchAppConfig();
    }
  };

  const wrapperToken = async (fetchConfig = true) => {
    try {
      const header = buildWrapperTokenHeader();
      const response = await postWebService("token.php", {}, header);

      cbSuccessWrapperToken(response.data, fetchConfig);
    } catch (error) {
      // console.log(error);
    }
  };

  const navigateTo = (pathname) => history.push({ pathname });

  const handleBack = () => navigateTo(getRoute("healthDeclaration"));

  const handleClose = (keyname = "open", flag = false) =>
    toggleModal(keyname, flag);

  const handleOnClose = (event, reason, modalKey, action = "") => {
    if (Utils.dialogOnCloseReasons.indexOf(reason) === -1) {
      toggleModal(modalKey, false);
      if (action === SLUG_RETRY) {
        window.location.reload();
      } else if (action === SLUG_TERMINATE) {
        cbTermimateJourney();
      }
    }
  };

  const toggleModal = (keyname = "open", flag = true) =>
    setModal({ ...modal, [keyname]: flag });

  const cbError = (error = "An error occured, please try again.") => {
    setIsFormSubmitted(false);
    setErrors(new Array(error));
  };

  const cbTerminateFaceMatch = () => {
    setStorageItem(
      "journeyCloseReason",
      "Journey terminated due to face mismatch."
    );
    navigateTo(getRoute("terminated"));
  };

  const cbSuccessFaceMatch = ({ data }, checksum) => {
    setIsFormSubmitted(false);
    let dataSlice = data.slice(9);
    dataSlice = dataSlice.slice(0, -8);

    const dataJSON = JSON.parse(atob(dataSlice));
    if (dataJSON.status == 200 && dataJSON.checksum == checksum) {
      const isValid = dataJSON?.data?.is_face_match ?? false;
      const faceScore = dataJSON?.data?.face_match_score ?? "";
      // console.log(`isValid = ${isValid}, faceScore = ${faceScore}`);

      isValid ? navigateTo(getRoute("declaration")) : cbTerminateFaceMatch();
    } else {
      cbTerminateFaceMatch();
    }
  };

  const checkFaceMatch = async () => {
    try {
      const checksum = makeid();
      const response = await postWebService("facematch.php", {
        oid: Number(getStorageItem("oid")),
        checksum,
      });

      cbSuccessFaceMatch(response.data, checksum);
    } catch (error) {
      cbError(error);
    }
  };

  const updateOrderDetails = async () => {
    try {
      setIsFormSubmitted(true);
      const response = await postWebService("update-order.php", {
        oid: Number(getStorageItem("oid")),
        status: "FEDO",
      });

      if (response.data.code === 201) {
        checkFaceMatch();
        // setIsFormSubmitted(false);
        // navigateTo(getRoute("declaration"));
      }
    } catch (error) {
      cbError(error);
    }
  };

  const toggleHealthCheck = () => {
    setIsFormSubmitted(false);
    setFedohealthcheck(true);
    setTimeout(() => setFedohealthcheck(false), 5000);
  };

  const cbTermimateJourney = () => {
    toggleHealthCheck();
    setStorageItem(
      "journeyCloseReason",
      "Journey terminated as health assessment has failed."
    );
    navigateTo(getRoute("terminated"));
  };

  const cbSuccessCheckHealthStatus = ({
    fedoBase64 = null,
    message = null,
    data = null,
  }) => {
    if (message === "Y") {
      data?.fedoBase64 ? updateOrderDetails() : toggleHealthCheck();
    } else if (message == "N") {
      setHealthVitals(data);
      setStorageItem("healthVitals", JSON.stringify(data));
      fetchOrderDetails();
    } else {
      toggleHealthCheck();
    }
  };

  const checkHealthStatus = async () => {
    try {
      setIsFormSubmitted(true);
      const response = await postWebService(
        "checkcareplixstatuswithvitals.php",
        {
          customerId: Number(getStorageItem("oid")),
          scanId: Number(getStorageItem("scanID")),
          oid: Number(getStorageItem("oid")),
        }
      );

      console.log(response.data);
      cbSuccessCheckHealthStatus(response.data);
    } catch (error) {
      cbError(error);
    }
  };

  const cbSuccessOrderDetails = ({ data }, checksum) => {
    let dataSlice = data.slice(9);
    dataSlice = dataSlice.slice(0, -8);

    const dataJSON = JSON.parse(atob(dataSlice));

    if (dataJSON.checksum == checksum) {
      dataJSON.health_failed_count >= 3
        ? cbTermimateJourney()
        : toggleModal(SLUG_RETRY, true);
    }
  };

  const fetchOrderDetails = async () => {
    try {
      const checksum = makeid();
      const response = await postWebService("orders.php", {
        ...buildCheckSumPayload(checksum),
        ...buildOrderIdPayload(checksum),
      });

      cbSuccessOrderDetails(response.data, checksum);
    } catch (error) {
      // console.log(error);
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();

    checkHealthStatus();
  };

  /* Start: Scan Now and Scan Link Section */
  const fetchAppConfig = async () => {
    const partnerid = getStorageItem("partnerId");
    const response = await postWebService("app_config.php", { partnerid });
    const { data } = response;

    setConfigLoaded(true);
    setHealthConfig(data?.HEALTH ? JSON.parse(data?.HEALTH) : null);
    getOrder();
  };

  const getOrder = useCallback(async () => {
    try {
      const checksum = makeid();
      const response = await postWebService("orders.php", {
        checksum,
        oid: getStorageItem("oid"),
      });
      const { data } = response;
      const str = data.data.slice(9, -8);
      const result = JSON.parse(atob(str));

      if (result.checksum === checksum) {
        toggleHealthBlock(result?.health_link ? "scan_link" : "options", true);
        setOrderDetails(result);
        setTimeout(() => setIsLoaded(true), 100);
      }
    } catch (error) {}
  }, []);

  const toggleHealthBlock = (key = "", value = false) => {
    setErrorMessage("");

    const newBlock = {
      ...healthBlock,
      options: false,
      scan_now: false,
      scan_link: false,
    }; // shallow copy
    newBlock[key] = value; // update the key

    setHealthBlock(newBlock);
  };

  const sendScanLink = async (flag = false) => {
    setErrorMessage("");
    setIsFormSubmitted(true);
    setResendLink(flag);

    const response = await postWebService("start-scan.php", {
      oid: localStorage.getItem("oid"),
      cid: localStorage.getItem("cid"),
      pid: localStorage.getItem("pid"),
      timestamps: Math.floor(Date.now() / 1000),
    });
    const { data } = response;
    setIsFormSubmitted(false);
    if (data?.error == "unauthorized") {
      wrapperToken(false);
      setErrorMessage("Unable to send scan link. Please try again.");
    } else {
      toggleHealthBlock("scan_link", true);
    }
  };
  /* End: Scan Now and Scan Link Section */

  return isLoaded && configLoaded ? (
    <Box className={main}>
      <Box container={"true"} justifyContent="center">
        <Box display="flex" justifyContent="center" m={2} mb={0}>
          <SectionTitle
            title={"Get a Health Assessment"}
            onClickHandler={handleBack}
          />
        </Box>

        {healthBlock?.options && (
          <Box className={formGroup}>
            {healthConfig?.scan_now && (
              <Box className={formGroup} textAlign={"left"}>
                <Typography variant="caption">
                  <b>Scan Now:</b> Use this option to perform the health
                  assessment if customer is around.
                </Typography>
              </Box>
            )}

            {healthConfig?.scan_now && healthConfig?.scan_link && (
              <Box
                className={formGroup}
                alignItems={"center"}
                sx={{ width: "100%" }}
              >
                <Typography variant="subtitle1">OR</Typography>
              </Box>
            )}

            {healthConfig?.scan_link && (
              <Box className={formGroup} textAlign={"left"}>
                <Typography variant="caption">
                  <b>Send Scan Link:</b> Use this option to conduct the health
                  assessment by sending a scan link to the customer via SMS.
                </Typography>
              </Box>
            )}

            <Box className={containerButton}>
              {errorMessage && (
                <Box mb={2}>
                  <Typography variant="caption" color="red">
                    {errorMessage}
                  </Typography>
                </Box>
              )}
              <CallToAction
                buttonType={"click"}
                errorMessage={errorMessage}
                form={form}
                isDisabled={false}
                isFormSubmitted={isFormSubmitted}
                text={"Scan Now"}
                handleClick={() => toggleHealthBlock("scan_now", true)}
              />

              <Box mt={1}>
                <CallToAction
                  buttonType={"click"}
                  errorMessage={errorMessage}
                  form={form}
                  isDisabled={false}
                  isFormSubmitted={isFormSubmitted}
                  text={"Send Scan Link"}
                  handleClick={() => sendScanLink()}
                  color={"secondary"}
                />
              </Box>
            </Box>
          </Box>
        )}

        {healthBlock?.scan_now && (
          <Box>
            <form noValidate autoComplete="off" onSubmit={onSubmit}>
              <Box mb={2}>
                <Box
                  display="flex"
                  justifyContent="center"
                  sx={{ position: "relative" }}
                >
                  <Box sx={{ height: "100vh", width: "100vw" }}>
                    {loading && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "70%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 1,
                        }}
                      >
                        <Typography variant="subtitle1">
                          Preparing your scan, please wait...
                        </Typography>
                      </Box>
                    )}

                    <iframe
                      id="iframeID"
                      allow="camera"
                      frameborder="0"
                      seamless
                      src={urlSafe}
                      height="100%"
                      width="98.9%"
                      wmode="transparent"
                      onLoad={() => setLoading(false)}
                      style={{ display: "block" }}
                    ></iframe>
                  </Box>
                </Box>
              </Box>

              {!modal.retry && (
                <Box className={containerButton}>
                  <CallToAction
                    buttonType={"submit"}
                    // errorMessage={errorMessage}
                    // form={form}
                    isDisabled={isFormSubmitted}
                    isFormSubmitted={isFormSubmitted}
                    text={"Proceed to Declaration"}
                  />
                </Box>
              )}
            </form>
          </Box>
        )}

        {healthBlock?.scan_link && (
          <Box className={formGroup}>
            {!fedohealthcheck && !errorMessage && (
              <Box className={formGroup} textAlign={"left"}>
                <Typography variant="caption">
                  <Alert icon={false} severity="success">
                    The health assessment link has been shared
                    {resendLink ? " again " : ""} with the customer. Kindly
                    verify and proceed once the customer has completed the
                    assessment using the provided link.
                  </Alert>
                </Typography>
              </Box>
            )}

            <Box className={containerButton}>
              {errorMessage && (
                <Box mb={2}>
                  <Typography variant="caption" color="red">
                    {errorMessage}
                  </Typography>
                </Box>
              )}
              <CallToAction
                buttonType={"click"}
                errorMessage={errorMessage}
                form={form}
                isDisabled={false}
                isFormSubmitted={isFormSubmitted}
                text={"Verify & Proceed"}
                handleClick={() => checkHealthStatus()}
              />

              <Box mt={1}>
                <CallToAction
                  buttonType={"click"}
                  errorMessage={errorMessage}
                  form={form}
                  isDisabled={false}
                  isFormSubmitted={isFormSubmitted}
                  text={"Re-Send Scan Link"}
                  handleClick={() => sendScanLink(true)}
                  color={"secondary"}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {modal.open && (
        <AssessmentGuideline
          modal={modal}
          toggleModal={toggleModal}
          onClose={handleOnClose}
        />
      )}

      {modal.retry && (
        <RetryAssessment
          modal={modal}
          toggleModal={toggleModal}
          onClose={handleOnClose}
        />
      )}

      {fedohealthcheck && (
        <NotificationMessage
          open={fedohealthcheck}
          message="The health assessment is currently awaited. Kindly wait."
          onClose={() => setFedohealthcheck(false)}
          severity="error"
        />
      )}
    </Box>
  ) : (
    <Box className={loaderBox}>
      <CircularProgress color={"secondary"} />
    </Box>
  );
}

export default withStyles(useStyles, { withTheme: true })(
  BuyflowHealthAssessment
);
